/* ELIO checkout intent + shallow-exit bridge v9
   Full replacement for assets/elio-checkout-intent.js

   Core rule:
   Only PAID META shallow visits can advance anti-recycling state.

   Repeat-shallow policy:
   1st paid-Meta shallow visit -> ELIO_Repeat_NoDepth_24h.
   That visitor becomes eligible again after the 24h cooldown.
   Next paid-Meta shallow visit after the cooldown, while the prior shallow
   visit is still within the 7d history -> ELIO_Repeat_NoDepth_3d.
   After a 3d exclusion expires, another paid-Meta shallow visit within 7d
   re-fires the 3d exclusion.
   ATC / checkout intent clears negative history.

   The 24h cooldown NEVER erases strike history and NEVER owns escalation.
   The storefront bridge is the SINGLE source of truth for strike state.
   The Shopify Custom Pixel mirrors explicitly classified 24h/3d events for
   browser/server dedupe; it does not run its own strike state machine.
*/

(() => {
  if (window.__ELIO_CHECKOUT_INTENT_LOADED__) return;
  window.__ELIO_CHECKOUT_INTENT_LOADED__ = true;

  const CHECKOUT_EVENT_NAME = 'elio:checkout_intent';
  const SHALLOW_EVENT_NAME = 'elio:shallow_exit';

  /* REQUIRED: replace with the deployed Vercel URL, e.g.
     https://elio-shallow-capi-xxxx.vercel.app/api/shallow */
  const CAPI_ENDPOINT = 'https://elioshallowcapivercelfixed.vercel.app/api/shallow';

  const SHALLOW_MS = 30000;
  const SESSION_GAP_MS = 30 * 60 * 1000;
  const POSITIVE_PROTECTION_MS = 30 * 24 * 60 * 60 * 1000;
  const DEDUPE_MS = 1500;
  const NEGATIVE_HISTORY_MS = 7 * 24 * 60 * 60 * 1000;
  const COOLDOWN_24H_MS = 24 * 60 * 60 * 1000;
  const SUPPRESS_3D_MS = 3 * 24 * 60 * 60 * 1000;

  const VISIT_KEY = 'elio_store_visit_v5';
  const POSITIVE_KEY = 'elio_positive_until_v1';
  const PIXEL_BACKUP_KEY = 'elio_shallow_pixel_backup_v2';
  const SERVER_BACKUP_KEY = 'elio_shallow_capi_backup_v2';
  const NODEPTH_STATE_KEY = 'elio_nodepth_repeat_v1';

  let lastKey = '';
  let lastAt = 0;
  let shallowPublished = false;
  let checkoutIntentSeen = false;
  let unloading = false;
  let internalNavigationUntil = 0;

  const STANDARD_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href="/checkout"]',
    'a[href$="/checkout"]',
    'a[href*="redirect=/checkout"]',
    '.js-checkout-button',
    '[data-elio-checkout]'
  ].join(',');

  const EXPRESS_TAGS = new Set([
    'shopify-apple-pay-button',
    'shop-pay-wallet-button',
    'shopify-google-pay-button',
    'shopify-paypal-button',
    'shopify-amazon-pay-button',
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart'
  ]);

  function normalized(value) {
    return String(value || '').trim().toLowerCase();
  }

  function nowMs() {
    return Date.now();
  }

  function makeVisitId() {
    return (
      'bridge-' +
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2, 10)
    );
  }

  function safeParse(raw) {
    try {
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }


  function defaultNoDepthState() {
    return {
      version: 2,
      strike_count: 0,
      last_shallow_at: 0,
      last_visit_id: '',
      cooldown_24h_until: 0,
      exclusion_3d_until: 0
    };
  }

  function readNoDepthState() {
    try {
      const raw =
        localStorage.getItem(
          NODEPTH_STATE_KEY
        );

      if (!raw) {
        return defaultNoDepthState();
      }

      const parsed = safeParse(raw);
      const base = defaultNoDepthState();

      if (!parsed || typeof parsed !== 'object') {
        return base;
      }

      Object.keys(base).forEach(key => {
        if (typeof parsed[key] !== 'undefined') {
          base[key] = parsed[key];
        }
      });

      /*
        v1 used one suppress_until field for the 3d exclusion. Preserve that
        as 3d state during migration, but do NOT turn it into a 24h cooldown.
      */
      if (
        Number(parsed.version || 1) < 2 &&
        Number(parsed.suppress_until || 0) > 0
      ) {
        base.exclusion_3d_until =
          Number(parsed.suppress_until || 0);
      }

      base.version = 2;

      return base;
    } catch (_) {
      return defaultNoDepthState();
    }
  }

  function writeNoDepthState(state) {
    try {
      localStorage.setItem(
        NODEPTH_STATE_KEY,
        JSON.stringify(state)
      );
    } catch (_) {}
  }

  function clearNoDepthState() {
    try {
      localStorage.removeItem(
        NODEPTH_STATE_KEY
      );
    } catch (_) {}
  }

  function classifyNoDepthPayload(payload) {
    if (!payload) return payload;

    const result = {
      ...payload,
      nodepth_classified: true,
      nodepth_action: 'observe_only',
      nodepth_strike: 0,
      approved_for_meta: false
    };

    if (payload.paid_meta !== true) {
      result.nodepth_action =
        'observe_non_meta';
      return result;
    }

    const visitId =
      String(
        payload.visit_id || ''
      );

    if (!visitId) {
      result.nodepth_action =
        'observe_missing_visit_id';
      return result;
    }

    const now = nowMs();
    const state = readNoDepthState();

    if (
      state.last_visit_id &&
      state.last_visit_id === visitId
    ) {
      result.nodepth_action =
        'duplicate_visit';
      result.nodepth_strike =
        Number(state.strike_count || 0);
      result.cooldown_until =
        Number(state.cooldown_24h_until || 0);
      result.suppress_until =
        Number(state.exclusion_3d_until || 0);
      return result;
    }

    const previousShallowAt =
      Number(
        state.last_shallow_at || 0
      );

    const withinHistory =
      previousShallowAt > 0 &&
      now - previousShallowAt <=
        NEGATIVE_HISTORY_MS;

    /*
      If Meta somehow serves again before the 24h audience has fully caught
      up, ignore that shallow visit for escalation. It does not extend the
      cooldown and does not advance strike history.
    */
    if (
      withinHistory &&
      Number(
        state.cooldown_24h_until || 0
      ) > now
    ) {
      state.last_visit_id = visitId;
      writeNoDepthState(state);

      result.nodepth_action =
        'cooldown_24h_active';
      result.nodepth_strike =
        Number(state.strike_count || 1);
      result.cooldown_until =
        Number(state.cooldown_24h_until || 0);
      return result;
    }

    /*
      Same guard for an active 3d exclusion: do not let accidental delivery
      during the exclusion mutate or extend the strike ladder.
    */
    if (
      withinHistory &&
      Number(
        state.exclusion_3d_until || 0
      ) > now
    ) {
      state.last_visit_id = visitId;
      writeNoDepthState(state);

      result.nodepth_action =
        'suppression_3d_active';
      result.nodepth_strike =
        Number(state.strike_count || 2);
      result.suppress_until =
        Number(state.exclusion_3d_until || 0);
      return result;
    }

    state.last_visit_id = visitId;

    /*
      No live negative history: this is strike 1. Fire the 24h event, but
      preserve the strike record independently so the later 3d escalation
      still works after the cooldown expires.
    */
    if (!withinHistory) {
      state.strike_count = 1;
      state.last_shallow_at = now;
      state.cooldown_24h_until =
        now + COOLDOWN_24H_MS;
      state.exclusion_3d_until = 0;

      writeNoDepthState(state);

      result.nodepth_action =
        'exclude_24h';
      result.nodepth_strike = 1;
      result.cooldown_until =
        state.cooldown_24h_until;
      result.suppress_until =
        state.cooldown_24h_until;
      result.exclusion_event =
        'ELIO_Repeat_NoDepth_24h';
      result.exclusion_event_id =
        'elio-shallow-24h-' + visitId;
      result.approved_for_meta = true;
      return result;
    }

    /*
      Prior shallow history exists and both exclusion windows are over.
      This is the repeat-shallow escalation to 3d.
    */
    state.strike_count =
      Math.max(
        2,
        Number(
          state.strike_count || 0
        ) + 1
      );

    state.last_shallow_at = now;
    state.cooldown_24h_until = 0;
    state.exclusion_3d_until =
      now + SUPPRESS_3D_MS;

    writeNoDepthState(state);

    result.nodepth_action =
      'exclude_3d';
    result.nodepth_strike =
      state.strike_count;
    result.suppress_until =
      state.exclusion_3d_until;
    result.exclusion_event =
      'ELIO_Repeat_NoDepth_3d';
    result.exclusion_event_id =
      'elio-shallow-3d-' + visitId;
    result.approved_for_meta = true;

    return result;
  }

  function cookieValue(name) {
    try {
      const target = String(name || '') + '=';
      const parts = String(document.cookie || '').split(';');

      for (const part of parts) {
        const trimmed = part.trim();

        if (trimmed.indexOf(target) === 0) {
          return decodeURIComponent(
            trimmed.slice(target.length)
          );
        }
      }
    } catch (_) {}

    return '';
  }

  function capiConfigured() {
    return (
      CAPI_ENDPOINT &&
      CAPI_ENDPOINT.indexOf('REPLACE_WITH_') !== 0 &&
      /^https:\/\//i.test(CAPI_ENDPOINT)
    );
  }

  function marketingTrackingAllowed() {
    try {
      const cp =
        window.Shopify?.customerPrivacy;

      if (!cp) return true;

      if (
        typeof cp.marketingAllowed ===
        'function'
      ) {
        return (
          cp.marketingAllowed() === true
        );
      }

      if (
        typeof cp.analyticsProcessingAllowed ===
        'function'
      ) {
        return (
          cp.analyticsProcessingAllowed() ===
          true
        );
      }
    } catch (_) {}

    return true;
  }

  function getPositiveRecord() {
    try {
      const raw =
        localStorage.getItem(
          POSITIVE_KEY
        );

      if (!raw) return null;

      const parsed =
        safeParse(raw);

      if (!parsed) return null;

      const until =
        Number(
          parsed.until || 0
        );

      if (until <= nowMs()) {
        localStorage.removeItem(
          POSITIVE_KEY
        );

        return null;
      }

      return parsed;

    } catch (_) {
      return null;
    }
  }

  function hasPositiveProtection() {
    return Boolean(
      getPositiveRecord()
    );
  }

  function writePositiveProtection(
    reason
  ) {
    clearNoDepthState();

    try {
      localStorage.setItem(
        POSITIVE_KEY,
        JSON.stringify({
          until:
            nowMs() +
            POSITIVE_PROTECTION_MS,

          reason:
            reason || 'positive',

          at:
            nowMs()
        })
      );
    } catch (_) {}
  }

  function trafficMetadata(
    rawUrl,
    rawReferrer
  ) {
    const metadata = {
      traffic_source: 'direct',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      fbclid: '',
      referrer:
        rawReferrer || ''
    };

    let url = null;

    try {
      url = new URL(
        rawUrl,
        window.location.href
      );
    } catch (_) {}

    if (url) {
      metadata.utm_source =
        url.searchParams.get(
          'utm_source'
        ) || '';

      metadata.utm_medium =
        url.searchParams.get(
          'utm_medium'
        ) || '';

      metadata.utm_campaign =
        url.searchParams.get(
          'utm_campaign'
        ) || '';

      metadata.fbclid =
        url.searchParams.get(
          'fbclid'
        ) || '';
    }

    const source =
      normalized(
        metadata.utm_source
      );

    const medium =
      normalized(
        metadata.utm_medium
      ).replace(/[- ]/g, '_');

    const sourceIsMeta =
      source === 'facebook' ||
      source === 'fb' ||
      source === 'instagram' ||
      source === 'ig' ||
      source === 'meta';

    const mediumIsPaid =
      medium === 'paid' ||
      medium === 'paid_social' ||
      medium === 'paidsocial' ||
      medium === 'cpc';

    if (
      metadata.fbclid ||
      (
        sourceIsMeta &&
        mediumIsPaid
      )
    ) {
      metadata.traffic_source =
        'meta_paid';

      return metadata;
    }

    if (source) {
      metadata.traffic_source =
        source;

      return metadata;
    }

    if (rawReferrer) {
      try {
        const referrerUrl =
          new URL(
            rawReferrer
          );

        const host =
          normalized(
            referrerUrl.hostname
          );

        if (
          host.includes(
            'facebook.com'
          )
        ) {
          metadata.traffic_source =
            'facebook';

        } else if (
          host.includes(
            'instagram.com'
          )
        ) {
          metadata.traffic_source =
            'instagram';

        } else if (
          host.includes(
            'google.'
          )
        ) {
          metadata.traffic_source =
            'google';

        } else if (
          host.includes(
            'bing.com'
          )
        ) {
          metadata.traffic_source =
            'bing';

        } else {
          metadata.traffic_source =
            host || 'referral';
        }

        return metadata;

      } catch (_) {
        metadata.traffic_source =
          'referral';
      }
    }

    return metadata;
  }

  function readVisit() {
    try {
      const raw =
        sessionStorage.getItem(
          VISIT_KEY
        );

      if (!raw) return null;

      const visit =
        safeParse(raw);

      if (
        !visit ||
        !visit.visit_id ||
        !visit.started_at
      ) {
        return null;
      }

      return visit;

    } catch (_) {
      return null;
    }
  }

  function writeVisit(visit) {
    if (!visit) return;

    try {
      sessionStorage.setItem(
        VISIT_KEY,
        JSON.stringify(visit)
      );
    } catch (_) {}
  }

  function clearVisit() {
    try {
      sessionStorage.removeItem(
        VISIT_KEY
      );
    } catch (_) {}
  }

  function getOrStartVisit() {
    const now = nowMs();

    let visit =
      readVisit();

    if (visit) {
      const lastSeen =
        Number(
          visit.last_seen_at ||
          visit.started_at ||
          0
        );

      if (
        lastSeen > 0 &&
        now - lastSeen <
          SESSION_GAP_MS
      ) {
        visit.last_seen_at = now;

        visit.current_path =
          window.location.pathname;

        if (
          hasPositiveProtection()
        ) {
          visit.positive = true;

          visit.positive_reason =
            visit.positive_reason ||
            'prior_positive_protection';
        }

        writeVisit(visit);

        return visit;
      }

      clearVisit();
      visit = null;
    }

    const traffic =
      trafficMetadata(
        window.location.href,
        document.referrer || ''
      );

    visit = {
      version: 5,

      visit_id:
        makeVisitId(),

      started_at:
        now,

      last_seen_at:
        now,

      landing_url:
        window.location.href,

      landing_path:
        window.location.pathname,

      current_path:
        window.location.pathname,

      traffic_source:
        traffic.traffic_source,

      utm_source:
        traffic.utm_source,

      utm_medium:
        traffic.utm_medium,

      utm_campaign:
        traffic.utm_campaign,

      fbclid:
        traffic.fbclid,

      referrer:
        traffic.referrer,

      paid_meta:
        traffic.traffic_source ===
        'meta_paid',

      shallow_sent:
        false,

      positive:
        hasPositiveProtection(),

      positive_reason:
        hasPositiveProtection()
          ? 'prior_positive_protection'
          : ''
    };

    writeVisit(visit);

    return visit;
  }

  let activeVisit =
    getOrStartVisit();

  function refreshVisit() {
    const visit =
      readVisit();

    if (visit) {
      activeVisit = visit;
    }

    return activeVisit;
  }

  function elapsedForVisit(
    visit
  ) {
    if (
      !visit ||
      !visit.started_at
    ) {
      return 0;
    }

    return Math.max(
      0,
      nowMs() -
        Number(
          visit.started_at
        )
    );
  }

  function markVisitPositive(
    reason
  ) {
    /*
      v7: ATC is restored as durable positive evidence. Historical ELIO data
      shows high ATC is generally favorable; the failure mode is ATC without
      checkout follow-through, which is not a reason to classify that visitor
      as shallow. Dwell time remains neutral in the Custom Pixel.
    */
    writePositiveProtection(
      reason
    );

    const visit =
      refreshVisit();

    if (!visit) return;

    visit.positive = true;

    visit.positive_reason =
      reason || 'positive';

    visit.positive_at =
      nowMs();

    visit.last_seen_at =
      nowMs();

    writeVisit(visit);

    activeVisit = visit;
  }

  function publishShopifyEvent(
    name,
    payload
  ) {
    try {
      const result =
        window.Shopify?.analytics?.publish?.(
          name,
          payload
        );

      if (
        result &&
        typeof result.catch ===
          'function'
      ) {
        result.catch(
          () => {}
        );
      }
    } catch (_) {}
  }

  function elementsInPath(
    event
  ) {
    const raw =
      typeof event.composedPath ===
      'function'
        ? event.composedPath()
        : [];

    const els =
      raw.filter(
        node =>
          node &&
          node.nodeType === 1
      );

    if (
      !els.length &&
      event.target &&
      event.target.nodeType === 1
    ) {
      els.push(
        event.target
      );
    }

    return els;
  }

  function sourceFor(el) {
    if (
      el?.closest?.(
        '#cartDrawer, sht-cart-drwr, .js-cart-drawer-wrapper'
      )
    ) {
      return 'cart_drawer';
    }

    if (
      el?.closest?.(
        '[data-elio-checkout-nudge], .elio-checkout-nudge'
      )
    ) {
      return 'checkout_nudge';
    }

    if (
      el?.closest?.(
        '.elio-cart-page, [id^="ElioCartForm-"]'
      )
    ) {
      return 'cart_page';
    }

    if (
      el?.closest?.(
        '.elio-pdp, [data-elio-purchase-form], .product-form, .shopify-section-product'
      )
    ) {
      return 'pdp';
    }

    if (
      window.location.pathname.indexOf(
        '/products/'
      ) === 0
    ) {
      return 'pdp';
    }

    if (
      window.location.pathname ===
      '/cart'
    ) {
      return 'cart_page';
    }

    return 'storefront';
  }

  function methodFromElement(
    el
  ) {
    const tag =
      (
        el?.tagName || ''
      ).toLowerCase();

    const text =
      (
        (
          el?.getAttribute?.(
            'aria-label'
          ) || ''
        ) +
        ' ' +
        (
          el?.textContent ||
          ''
        ) +
        ' ' +
        tag
      ).toLowerCase();

    if (
      tag.includes(
        'apple-pay'
      ) ||
      text.includes(
        'apple pay'
      )
    ) {
      return 'apple_pay';
    }

    if (
      tag.includes(
        'shop-pay'
      ) ||
      text.includes(
        'shop pay'
      )
    ) {
      return 'shop_pay';
    }

    if (
      tag.includes(
        'google-pay'
      ) ||
      text.includes(
        'google pay'
      )
    ) {
      return 'google_pay';
    }

    if (
      tag.includes(
        'paypal'
      ) ||
      text.includes(
        'paypal'
      )
    ) {
      return 'paypal';
    }

    if (
      tag.includes(
        'amazon'
      ) ||
      text.includes(
        'amazon pay'
      )
    ) {
      return 'amazon_pay';
    }

    if (
      text.includes(
        'venmo'
      )
    ) {
      return 'venmo';
    }

    if (
      el?.matches?.(
        STANDARD_SELECTOR
      )
    ) {
      return 'standard_checkout';
    }

    return 'accelerated_checkout';
  }

  function acceleratedElement(
    event
  ) {
    const path =
      elementsInPath(
        event
      );

    for (
      const el of path
    ) {
      const tag =
        (
          el.tagName || ''
        ).toLowerCase();

      if (
        EXPRESS_TAGS.has(
          tag
        )
      ) {
        return el;
      }

      if (
        el.matches?.(
          '.shopify-payment-button__button, [data-shopify="payment-button"]'
        )
      ) {
        return el;
      }
    }

    for (
      const el of path
    ) {
      if (
        el.matches?.(
          '[data-shopify-buttoncontainer]'
        )
      ) {
        return el;
      }

      if (
        el.matches?.(
          '.shopify-payment-button'
        )
      ) {
        return el;
      }
    }

    return null;
  }

  function explicitElioCheckout(
    event
  ) {
    const target =
      event.target?.closest?.(
        '.elio-pdp__sticky-atc.elio-flip-stack .elio-pdp__sticky-button,' +
        '.elio-pc-modal__skip,' +
        '.elio-pc-modal__add,' +
        '[data-elio-nudge-decline]'
      );

    return target || null;
  }

  function publishCheckout(
    source,
    method,
    trigger
  ) {
    const now = nowMs();

    const key =
      source +
      '|' +
      method;

    if (
      key === lastKey &&
      now - lastAt <
        DEDUPE_MS
    ) {
      return;
    }

    lastKey = key;
    lastAt = now;

    checkoutIntentSeen =
      true;

    markVisitPositive(
      'checkout_intent'
    );

    const visit =
      refreshVisit();

    const payload = {
      source,
      method,

      event_id:
        'elio-checkout-' +
        (
          visit?.visit_id ||
          'unknown'
        ) +
        '-' +
        String(now),

      path:
        window.location.pathname,

      currency:
        window.Shopify
          ?.currency
          ?.active ||
        window.Shopify
          ?.currency ||
        null
    };

    try {
      document.dispatchEvent(
        new CustomEvent(
          'elio:checkout_intent',
          {
            detail:
              payload
          }
        )
      );
    } catch (_) {}

    publishShopifyEvent(
      CHECKOUT_EVENT_NAME,
      payload
    );

    try {
      if (
        trigger?.dataset
      ) {
        trigger.dataset.elioCheckoutIntentPublished =
          String(now);
      }
    } catch (_) {}
  }

  function markInternalNavigation() {
    internalNavigationUntil =
      nowMs() + 3000;
  }

  function internalNavigationPending() {
    return (
      nowMs() <
      internalNavigationUntil
    );
  }

  function sameOriginUrl(
    rawUrl
  ) {
    if (!rawUrl) return false;

    try {
      const target =
        new URL(
          rawUrl,
          window.location.href
        );

      return (
        target.origin ===
        window.location.origin
      );

    } catch (_) {
      return false;
    }
  }

  function isCartAddUrl(
    rawUrl
  ) {
    if (!rawUrl) return false;

    try {
      const target =
        new URL(
          rawUrl,
          window.location.href
        );

      return (
        target.origin ===
          window.location.origin &&
        /\/cart\/add(?:\.js)?\/?$/i.test(
          target.pathname
        )
      );

    } catch (_) {
      return false;
    }
  }

  function markInternalAnchorNavigation(
    event
  ) {
    const anchor =
      event.target?.closest?.(
        'a[href]'
      );

    if (!anchor) return;

    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button === 1 ||
      anchor.target ===
        '_blank' ||
      anchor.hasAttribute(
        'download'
      )
    ) {
      return;
    }

    if (
      anchor.matches?.(
        STANDARD_SELECTOR
      )
    ) {
      return;
    }

    if (
      sameOriginUrl(
        anchor.href
      )
    ) {
      markInternalNavigation();
    }
  }

  try {
    if (
      window.navigation &&
      typeof window.navigation
        .addEventListener ===
        'function'
    ) {
      window.navigation
        .addEventListener(
          'navigate',
          event => {
            try {
              const destination =
                event.destination
                  ?.url;

              if (
                destination &&
                sameOriginUrl(
                  destination
                )
              ) {
                markInternalNavigation();
              }
            } catch (_) {}
          }
        );
    }
  } catch (_) {}

  function shallowPayload(
    reason
  ) {
    const visit =
      refreshVisit();

    if (!visit) return null;

    return {
      visit_id:
        visit.visit_id,

      reason:
        reason ||
        'shallow_exit',

      elapsed_ms:
        Math.max(
          1,
          Math.round(
            elapsedForVisit(
              visit
            )
          )
        ),

      path:
        window.location.pathname,

      href:
        window.location.href,

      event_source_url:
        window.location.href,

      landing_url:
        visit.landing_url ||
        '',

      referrer:
        visit.referrer ||
        document.referrer ||
        '',

      traffic_source:
        visit.traffic_source ||
        'unknown',

      utm_source:
        visit.utm_source ||
        '',

      utm_medium:
        visit.utm_medium ||
        '',

      utm_campaign:
        visit.utm_campaign ||
        '',

      fbclid:
        visit.fbclid ||
        '',

      paid_meta:
        visit.paid_meta ===
        true,

      fbp:
        cookieValue(
          '_fbp'
        ),

      fbc:
        cookieValue(
          '_fbc'
        ),

      event_time:
        Math.floor(
          nowMs() / 1000
        )
    };
  }

  function writeBackup(
    key,
    payload
  ) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify(payload)
      );
    } catch (_) {}
  }

  function removeBackupIfVisitMatches(
    key,
    visitId
  ) {
    try {
      const raw =
        localStorage.getItem(
          key
        );

      if (!raw) return;

      const current =
        safeParse(raw);

      if (
        current &&
        current.visit_id ===
          visitId
      ) {
        localStorage.removeItem(
          key
        );
      }
    } catch (_) {}
  }

  function sendServerShallow(
    payload,
    useBeacon
  ) {
    if (
      !payload ||
      !payload.visit_id
    ) {
      return;
    }

    const approvedTier =
      (
        payload.nodepth_action === 'exclude_24h' &&
        payload.exclusion_event === 'ELIO_Repeat_NoDepth_24h'
      ) ||
      (
        payload.nodepth_action === 'exclude_3d' &&
        payload.exclusion_event === 'ELIO_Repeat_NoDepth_3d'
      );

    if (
      payload.approved_for_meta !== true ||
      payload.paid_meta !== true ||
      !approvedTier
    ) {
      return;
    }

    if (
      !capiConfigured()
    ) {
      return;
    }

    if (
      !marketingTrackingAllowed()
    ) {
      return;
    }

    writeBackup(
      SERVER_BACKUP_KEY,
      payload
    );

    const body =
      JSON.stringify(
        payload
      );

    /*
      One CAPI request only. fetch keepalive is unload-safe and the local
      backup is replayed on the next page if this request never completes.
      This avoids the old beacon+fetch duplicate request pattern.
    */
    try {
      fetch(
        CAPI_ENDPOINT,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'text/plain;charset=UTF-8'
          },

          body,

          keepalive: true,

          mode:
            'cors',

          credentials:
            'omit'
        }
      )
        .then(
          response => {
            if (
              response &&
              response.ok
            ) {
              removeBackupIfVisitMatches(
                SERVER_BACKUP_KEY,
                payload.visit_id
              );
            }
          }
        )
        .catch(
          () => {}
        );

    } catch (_) {}
  }

  function publishShallowExit(
    reason
  ) {
    const visit =
      refreshVisit();

    if (!visit) return;
    if (shallowPublished) return;
    if (visit.shallow_sent) return;
    if (visit.positive) return;

    if (
      hasPositiveProtection()
    ) {
      return;
    }

    if (
      checkoutIntentSeen
    ) {
      return;
    }

    if (
      internalNavigationPending()
    ) {
      return;
    }

    const elapsed =
      elapsedForVisit(
        visit
      );

    if (
      elapsed <= 0 ||
      elapsed >= SHALLOW_MS
    ) {
      return;
    }

    shallowPublished = true;
    visit.shallow_sent = true;
    visit.last_seen_at = nowMs();
    writeVisit(visit);
    activeVisit = visit;

    let payload =
      shallowPayload(
        reason ||
        'shallow_exit'
      );

    if (!payload) return;

    payload =
      classifyNoDepthPayload(
        payload
      );

    writeBackup(
      PIXEL_BACKUP_KEY,
      payload
    );

    try {
      document.dispatchEvent(
        new CustomEvent(
          'elio:shallow_exit',
          {
            detail:
              payload
          }
        )
      );
    } catch (_) {}

    publishShopifyEvent(
      SHALLOW_EVENT_NAME,
      payload
    );

    if (
      payload.nodepth_action ===
        'exclude_24h' ||
      payload.nodepth_action ===
        'exclude_3d'
    ) {
      sendServerShallow(
        payload,
        true
      );
    }
  }

  function stageShallowBackup(
    reason
  ) {
    const visit =
      refreshVisit();

    if (!visit) return;
    if (shallowPublished) return;
    if (visit.shallow_sent) return;
    if (visit.positive) return;

    if (
      hasPositiveProtection()
    ) {
      return;
    }

    if (
      checkoutIntentSeen
    ) {
      return;
    }

    if (
      internalNavigationPending()
    ) {
      return;
    }

    const elapsed =
      elapsedForVisit(
        visit
      );

    if (
      elapsed <= 0 ||
      elapsed >= SHALLOW_MS
    ) {
      return;
    }

    const payload =
      shallowPayload(
        reason ||
        'visibility_hidden'
      );

    if (!payload) return;

    writeBackup(
      PIXEL_BACKUP_KEY,
      payload
    );
  }

  function clearStagedBackupsIfStillHere() {
    if (unloading) return;

    const visit =
      refreshVisit();

    if (!visit) return;

    removeBackupIfVisitMatches(
      PIXEL_BACKUP_KEY,
      visit.visit_id
    );

    removeBackupIfVisitMatches(
      SERVER_BACKUP_KEY,
      visit.visit_id
    );
  }

  function replayPixelBackup() {
    let raw = null;

    try {
      raw =
        localStorage.getItem(
          PIXEL_BACKUP_KEY
        );

      if (!raw) return;

      localStorage.removeItem(
        PIXEL_BACKUP_KEY
      );

    } catch (_) {
      return;
    }

    let payload =
      safeParse(raw);

    if (
      !payload ||
      !payload.visit_id
    ) {
      return;
    }

    if (
      payload.nodepth_classified !== true
    ) {
      payload =
        classifyNoDepthPayload(
          payload
        );
    }

    publishShopifyEvent(
      SHALLOW_EVENT_NAME,
      payload
    );

    if (
      payload.nodepth_action ===
        'exclude_24h' ||
      payload.nodepth_action ===
        'exclude_3d'
    ) {
      sendServerShallow(
        payload,
        false
      );
    }
  }

  function replayServerBackup() {
    let raw = null;

    try {
      raw =
        localStorage.getItem(
          SERVER_BACKUP_KEY
        );

      if (!raw) return;

    } catch (_) {
      return;
    }

    const payload =
      safeParse(raw);

    const approvedTier =
      payload &&
      (
        (
          payload.nodepth_action === 'exclude_24h' &&
          payload.exclusion_event === 'ELIO_Repeat_NoDepth_24h'
        ) ||
        (
          payload.nodepth_action === 'exclude_3d' &&
          payload.exclusion_event === 'ELIO_Repeat_NoDepth_3d'
        )
      );

    if (
      !payload ||
      !payload.visit_id ||
      payload.approved_for_meta !== true ||
      payload.paid_meta !== true ||
      !approvedTier
    ) {
      try {
        localStorage.removeItem(
          SERVER_BACKUP_KEY
        );
      } catch (_) {}
      return;
    }

    sendServerShallow(
      payload,
      false
    );
  }

  /*
    Successful AJAX /cart/add protection.
  */

  try {
    const nativeFetch =
      window.fetch;

    if (
      typeof nativeFetch ===
      'function'
    ) {
      window.fetch =
        function () {
          const args =
            arguments;

          const input =
            args[0];

          const init =
            args[1] || {};

          const rawUrl =
            typeof input ===
            'string'
              ? input
              : input?.url;

          const method =
            normalized(
              init.method ||
              input?.method ||
              'get'
            );

          const isCartAdd =
            method === 'post' &&
            isCartAddUrl(
              rawUrl
            );

          return nativeFetch
            .apply(
              this,
              args
            )
            .then(
              response => {
                if (
                  isCartAdd &&
                  response &&
                  response.ok
                ) {
                  markVisitPositive(
                    'add_to_cart'
                  );
                }

                return response;
              }
            );
        };
    }
  } catch (_) {}

  try {
    const nativeOpen =
      XMLHttpRequest
        .prototype
        .open;

    const nativeSend =
      XMLHttpRequest
        .prototype
        .send;

    XMLHttpRequest
      .prototype
      .open =
        function (
          method,
          url
        ) {
          try {
            this.__elioCartAdd =
              normalized(
                method
              ) === 'post' &&
              isCartAddUrl(
                url
              );

          } catch (_) {
            this.__elioCartAdd =
              false;
          }

          return nativeOpen.apply(
            this,
            arguments
          );
        };

    XMLHttpRequest
      .prototype
      .send =
        function () {
          if (
            this.__elioCartAdd
          ) {
            try {
              this.addEventListener(
                'load',
                () => {
                  if (
                    this.status >=
                      200 &&
                    this.status <
                      400
                  ) {
                    markVisitPositive(
                      'add_to_cart'
                    );
                  }
                },
                {
                  once: true
                }
              );
            } catch (_) {}
          }

          return nativeSend.apply(
            this,
            arguments
          );
        };

  } catch (_) {}

  function handleClick(
    event
  ) {
    markInternalAnchorNavigation(
      event
    );

    const express =
      acceleratedElement(
        event
      );

    if (express) {
      publishCheckout(
        sourceFor(express),
        methodFromElement(
          express
        ),
        express
      );

      return;
    }

    const elio =
      explicitElioCheckout(
        event
      );

    if (elio) {
      publishCheckout(
        sourceFor(elio),
        'standard_checkout',
        elio
      );

      return;
    }

    const standard =
      event.target?.closest?.(
        STANDARD_SELECTOR
      );

    if (standard) {
      publishCheckout(
        sourceFor(
          standard
        ),
        methodFromElement(
          standard
        ),
        standard
      );
    }
  }

  document.addEventListener(
    'click',
    handleClick,
    true
  );

  document.addEventListener(
    'submit',
    event => {
      const form =
        event.target;

      if (
        !form ||
        !form.matches?.(
          'form'
        )
      ) {
        return;
      }

      try {
        const action =
          form.getAttribute(
            'action'
          ) ||
          window.location.href;

        if (
          isCartAddUrl(
            action
          )
        ) {
          markVisitPositive(
            'add_to_cart'
          );

          return;
        }
      } catch (_) {}

      const active =
        document.activeElement;

      let checkout =
        null;

      if (
        active &&
        active.matches?.(
          STANDARD_SELECTOR
        )
      ) {
        checkout = active;

      } else {
        checkout =
          form.querySelector?.(
            STANDARD_SELECTOR
          );
      }

      if (checkout) {
        publishCheckout(
          sourceFor(
            checkout
          ),
          'standard_checkout',
          checkout
        );

        return;
      }

      try {
        const action =
          form.getAttribute(
            'action'
          ) ||
          window.location.href;

        if (
          sameOriginUrl(
            action
          )
        ) {
          markInternalNavigation();
        }
      } catch (_) {}
    },
    true
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState ===
        'hidden'
      ) {
        stageShallowBackup(
          'visibility_hidden'
        );

      } else if (
        document.visibilityState ===
        'visible'
      ) {
        clearStagedBackupsIfStillHere();
      }
    },
    true
  );

  window.addEventListener(
    'beforeunload',
    () => {
      unloading = true;

      publishShallowExit(
        'beforeunload'
      );
    },
    {
      capture: true
    }
  );

  window.addEventListener(
    'pagehide',
    () => {
      unloading = true;

      publishShallowExit(
        'pagehide'
      );
    },
    {
      capture: true
    }
  );

  replayPixelBackup();
  replayServerBackup();

  window.elioCheckoutIntent = {
    publish: (
      source = 'storefront',
      method = 'standard_checkout'
    ) =>
      publishCheckout(
        source,
        method,
        null
      )
  };
})();